/**
 * Public waiting-room shown when a user opens a World Cup squad invite link
 * (e.g. https://zporti.com/wc-invite/<code>). Resolves the squad via the
 * public lookup endpoint, then renders the shared TicketStub branded with the
 * org's logo + colors (when the squad is an org default).
 *
 * The primary CTA (tear-off) routes to sign-up with `returnUrl` pointing at
 * the auto-join screen so the user lands directly in the squad after auth.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, Platform, Pressable, Text } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { useRouter } from 'expo-router';
import { useTheme } from '../../../src/theme/useTheme';
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations';
import { useSupabaseAuth } from '../../../src/shared/contexts/SupabaseAuthContext';
import { useEntryIntent } from '../../../src/onboarding/hooks/useEntryIntent';
import { ENTRY_INTENT_KIND } from '../../../src/onboarding/types/entryIntent.types';
import { WORLD_CUP_COMPETITION_ID } from '../../../src/worldcup/worldcup.constants';
import {
  competitionBasePath,
  competitionGroupJoinRoute,
} from '../../../src/competitions/helpers/competitionRoutes.helper';
import {
  fetchPublicSquadByCode,
  type PublicSquadLookupResponse,
} from '../../../src/onboarding/helpers/onboardingPublicApi';
import { buildPublicSignInHref } from '../../../src/signIn/helpers/postAuthRedirect.helper';
import { logger } from '../../../src/helpers/logger.helper';
import { detectMobileWebOs } from '../../../src/onboarding/helpers/detectMobileWebOs.helper';
import { TicketStub } from '../TicketStub/TicketStub';
import { getLocksPastLabel } from '../TicketStub/ticketStubLockIn.helper';
import type { TicketStubBrand } from '../TicketStub/TicketStub.types';
import { getWorldCupInviteWaitingRoomStyles } from './WorldCupInviteWaitingRoom.styles';
import { SHOW_CONTINUE_WITH_APP_CTA } from '../TicketStub/TicketStub.constants';
import {
  getWcInviteWaitingRoomSquadSubtitle,
  WC_INVITE_WAITING_ROOM_COPY,
} from './WorldCupInviteWaitingRoom.constants';

interface WorldCupInviteWaitingRoomProps {
  inviteCode: string;
  /** Which competition the squad belongs to; defaults to the World Cup for /wc-invite links. */
  competitionId?: string;
}

export function WorldCupInviteWaitingRoom({ inviteCode, competitionId = WORLD_CUP_COMPETITION_ID }: WorldCupInviteWaitingRoomProps) {
  const router = useRouter();
  const theme = useTheme();
  const styles = useMemo(() => getWorldCupInviteWaitingRoomStyles(theme), [theme]);
  const { setIntent } = useEntryIntent();
  const { isLoaded: authLoaded, isSignedIn, isAnonymous } = useSupabaseAuth();
  // A real (non-guest) signed-in user opening the invite must NOT be sent back through sign-in —
  // they tap "Join Squad" and go straight to the auto-join screen.
  const isAuthenticated = authLoaded && isSignedIn && !isAnonymous;

  const [squad, setSquad] = useState<PublicSquadLookupResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isResolving, setIsResolving] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setIsResolving(true);
    setError(null);
    void (async () => {
      try {
        const data = await fetchPublicSquadByCode(competitionId, inviteCode);
        if (cancelled) return;
        setSquad(data);
        setIntent(
          data.isOrgDefault && data.orgSlug
            ? {
                kind: ENTRY_INTENT_KIND.ORG,
                orgSlug: data.orgSlug,
                inviteCode,
                groupId: data.groupId,
                competitionId,
              }
            : {
                kind: ENTRY_INTENT_KIND.SQUAD_INVITE,
                inviteCode,
                groupId: data.groupId,
                competitionId,
              },
        );
      } catch (err) {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : WC_INVITE_WAITING_ROOM_COPY.ERROR_TITLE_FALLBACK;
        logger.warn('wc-invite waiting room resolve failed', { message });
        setError(message);
      } finally {
        if (!cancelled) setIsResolving(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [competitionId, inviteCode, setIntent]);

  const buildReturnUrl = useCallback(
    () => competitionGroupJoinRoute(competitionBasePath(competitionId), {
      code: inviteCode.toUpperCase(),
      autojoin: true,
    }),
    [competitionId, inviteCode],
  );

  const handleSignUp = useCallback(() => {
    router.replace(buildPublicSignInHref({ signup: true, returnUrl: buildReturnUrl() }) as `/${string}`);
  }, [buildReturnUrl, router]);

  const handleSignIn = useCallback(() => {
    router.replace(buildPublicSignInHref({ returnUrl: buildReturnUrl() }) as `/${string}`);
  }, [buildReturnUrl, router]);

  const handleJoinDirectly = useCallback(() => {
    router.replace(buildReturnUrl() as `/${string}`);
  }, [buildReturnUrl, router]);

  const hoverWeb = Platform.select({ web: WEB_HOVER_TRANSITION_STYLE });

  const fallbackGradient = useMemo<[string, string]>(
    () => [
      squad?.companyBranding?.primaryColor || theme.primary,
      squad?.companyBranding?.secondaryColor || theme.primaryLight || theme.primary,
    ],
    [squad, theme],
  );

  // Wait for auth to resolve too, so we never flash the "Create Account" CTA at an
  // already-signed-in user before swapping it for "Join Squad".
  if (isResolving || !authLoaded) {
    return (
      <LinearGradient colors={fallbackGradient} style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={theme.white} />
        <Text style={styles.loadingText}>{WC_INVITE_WAITING_ROOM_COPY.LOADING}</Text>
      </LinearGradient>
    );
  }

  if (error || !squad) {
    return (
      <LinearGradient colors={fallbackGradient} style={styles.loadingContainer}>
        <Text style={styles.errorTitle}>{error || WC_INVITE_WAITING_ROOM_COPY.ERROR_TITLE_FALLBACK}</Text>
        <Text style={styles.errorMessage}>
          {WC_INVITE_WAITING_ROOM_COPY.ERROR_MESSAGE}
        </Text>
        <Pressable
          onPress={() => router.replace('/' as `/${string}`)}
          style={({ pressed }) => [styles.primaryButton, hoverWeb, pressed && styles.pressed]}
        >
          <Text style={styles.primaryButtonText}>{WC_INVITE_WAITING_ROOM_COPY.GO_HOME_LABEL}</Text>
        </Pressable>
      </LinearGradient>
    );
  }

  const companyName = squad.companyBranding?.companyName ?? null;
  const brand: TicketStubBrand = {
    name: companyName || squad.name,
    primaryColor: squad.companyBranding?.primaryColor || theme.primary,
    secondaryColor: squad.companyBranding?.secondaryColor ?? null,
    logoUrl: squad.companyBranding?.logoUrl || squad.customLogoUrl || null,
  };

  return (
    <TicketStub
      brand={brand}
      presentedBy={companyName ? `${WC_INVITE_WAITING_ROOM_COPY.PRESENTED_BY_PREFIX}${companyName}` : null}
      title={`${WC_INVITE_WAITING_ROOM_COPY.JOIN_TITLE_PREFIX}${squad.name}`}
      subtitle={squad.description?.trim() || getWcInviteWaitingRoomSquadSubtitle(squad.competition?.name)}
      memberCount={squad.memberCount}
      memberCountLabel={`${squad.memberCount} ${squad.memberCount === 1 ? 'member' : 'members'} already in`}
      groupCode={inviteCode}
      matchCount={squad.competition?.matchCount ?? null}
      locksAtIso={squad.competition?.locksAt ?? null}
      locksPastLabel={getLocksPastLabel(squad.competition?.lifecycle)}
      primaryCta={
        isAuthenticated
          ? {
              label: WC_INVITE_WAITING_ROOM_COPY.JOIN_NOW_LABEL,
              testID: WC_INVITE_WAITING_ROOM_COPY.JOIN_NOW_TEST_ID,
              onPress: handleJoinDirectly,
            }
          : {
              label: WC_INVITE_WAITING_ROOM_COPY.CREATE_ACCOUNT_LABEL,
              testID: WC_INVITE_WAITING_ROOM_COPY.CREATE_ACCOUNT_TEST_ID,
              onPress: handleSignUp,
            }
      }
      disabledNotice={null}
      secondaryCta={
        isAuthenticated
          ? null
          : {
              label: WC_INVITE_WAITING_ROOM_COPY.SIGN_IN_LABEL,
              testID: WC_INVITE_WAITING_ROOM_COPY.SIGN_IN_TEST_ID,
              onPress: handleSignIn,
            }
      }
      showOpenInApp={
        SHOW_CONTINUE_WITH_APP_CTA && !isAuthenticated && detectMobileWebOs() !== null
      }
    />
  );
}
